import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, CheckCircle, Shield, Clock, Users, ArrowRight, Car, FileText, Stethoscope, Scale, AlertTriangle, Target, ExternalLink } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const RearEndAccidentPage = () => {
  const faqData = [
    {
      question: "Who is at fault in a rear-end collision?",
      answer: "In most cases, the driver who rear-ends another vehicle is presumed at fault. Washington law requires drivers to maintain a safe following distance. However, there can be exceptions—I'll investigate the full circumstances of your accident."
    },
    {
      question: "What injuries are common in rear-end accidents?",
      answer: "Whiplash is the most common, but rear-end collisions can cause herniated discs, traumatic brain injuries, back injuries, and soft tissue damage. Many injuries don't show symptoms immediately—get medical attention right away."
    },
    {
      question: "The insurance company says my injuries are minor. What should I do?",
      answer: "Don't accept their assessment. Insurance companies minimize claims to save money. I've seen 'minor' rear-end accidents cause serious, long-term injuries. Let me review your case before you settle."
    },
    {
      question: "How much is my rear-end accident case worth?",
      answer: "Value depends on injury severity, medical costs, lost wages, and pain and suffering. Rear-end accidents with soft tissue injuries can range from thousands to hundreds of thousands of dollars. I'll give you an honest assessment."
    },
    {
      question: "Should I accept a quick settlement offer after a rear-end collision?",
      answer: "Almost never. Quick offers are designed to close your claim before you understand the full extent of your injuries. Some injuries take weeks or months to fully develop. Talk to me before signing anything."
    }
  ];

  const processSteps = [
    {
      step: 1,
      title: "Free Consultation",
      description: "Call or text me directly. Tell me what happened and I'll give you honest advice about your options.",
      icon: Phone
    },
    {
      step: 2,
      title: "I Stop the Insurance Calls",
      description: "Once you hire me, the insurance company deals with me. No more pressure, no more stress.",
      icon: Shield
    },
    {
      step: 3,
      title: "You Focus on Healing",
      description: "Get the medical treatment you need for your whiplash, back pain, or other injuries. I handle the legal work.",
      icon: Stethoscope
    },
    {
      step: 4,
      title: "I Build Your Case",
      description: "I gather police reports, medical records, and evidence to prove the full extent of your injuries and damages.",
      icon: FileText
    },
    {
      step: 5,
      title: "You Decide",
      description: "I negotiate for maximum compensation. You decide whether to settle or go to trial. Your case, your choice.",
      icon: Scale
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="rear-end-accident-page">
      <SEOHead 
        title="Rear End Accident Lawyer | Whiplash & Back Injury Claims | Amy C. Brown Law"
        description="Injured in a rear-end collision? I fight for maximum compensation for whiplash, back injuries, and soft tissue damage. Former insurance defense attorney. No fees unless you win. Call (425) 675-1224."
        faqData={faqData}
        serviceData={{
          name: "Rear-End Collision Legal Representation",
          description: "Legal representation for rear-end accident victims in Washington State. Specializing in whiplash injuries, soft tissue damage, herniated discs, and insurance negotiations for maximum compensation.",
          category: "Personal Injury Law - Auto Accidents"
        }}
      />

      {/* Hero Section */}
      <section className="pt-36 pb-16 md:pt-40 md:pb-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Target className="w-4 h-4" />
              Rear-End Collision Attorney
            </div>
            
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-6 leading-tight">
              Rear-Ended and Hurting?
            </h1>
            
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              Your neck hurts. Your back aches. The insurance adjuster keeps calling. They sound friendly, but they're hoping you'll settle before you understand how serious your injuries really are.
            </p>

            <div className="flex flex-wrap gap-4">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold hover:bg-primary/90 transition-all"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center justify-center gap-2 border-2 border-secondary text-secondary px-8 py-4 rounded-full font-semibold hover:bg-secondary hover:text-white transition-all"
                data-testid="hero-text-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              Insurance Calling, Bills Arriving, Pain Not Going Away
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              I've helped countless people in your exact situation. Let me explain your options.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: AlertTriangle, title: "The Pain Isn't Going Away", desc: "Your neck and back still hurt. Maybe it's getting worse. This is common with rear-end injuries—and it's not something to ignore." },
              { icon: FileText, title: "Bills Keep Coming", desc: "Doctor visits, imaging, physical therapy—the costs pile up while you're trying to recover and wondering how you'll pay." },
              { icon: Clock, title: "The Adjuster Keeps Calling", desc: "They want a recorded statement. They sound helpful. But they're trained to find reasons to pay you less." },
              { icon: Car, title: "Their Offer Feels Wrong", desc: "Something tells you their 'fair' offer doesn't account for what you're really going through." }
            ].map((item, index) => (
              <div key={index} className="bg-slate-50 p-6 rounded-xl border border-slate-100">
                <item.icon className="w-10 h-10 text-primary mb-4" />
                <h3 className="font-heading font-semibold text-secondary mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-accent-soft rounded-2xl p-8 border-l-4 border-primary max-w-3xl mx-auto">
            <p className="text-slate-700 leading-relaxed">
              I'm more than an attorney—<strong>I've experienced trauma myself.</strong> Multiple members of my family were killed in a construction-related incident. I know pain, and I understand what you're going through right now. I'll listen, explain your options clearly, and handle the complexities so you can heal.
            </p>
          </div>
        </div>
      </section>

      {/* What Insurance Companies Don't Want You to Know */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-8 text-center">
              What Insurance Companies Don't Want You to Know About Rear-End Accidents
            </h2>
            
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="font-heading font-semibold text-secondary mb-2">Soft tissue injuries can be serious and long-lasting</h3>
                <p className="text-slate-600">Insurance companies dismiss whiplash as "minor." The truth? Soft tissue injuries can cause chronic pain, limited mobility, and ongoing medical treatment for years. I've won significant settlements for clients with these injuries.</p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="font-heading font-semibold text-secondary mb-2">Symptoms often get worse, not better</h3>
                <p className="text-slate-600">Many rear-end accident victims feel "okay" at first, then develop serious symptoms days or weeks later. Quick settlements are designed to close your claim before you understand the full extent of your injuries.</p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="font-heading font-semibold text-secondary mb-2">Low-speed impacts can cause significant injuries</h3>
                <p className="text-slate-600">Insurers argue that low-speed collisions can't cause real injuries. This is false. Your body can suffer significant trauma even in "minor" collisions. I know how to counter this argument.</p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="font-heading font-semibold text-secondary mb-2">Recorded statements are used against you</h3>
                <p className="text-slate-600">When an adjuster asks for a "routine" recorded statement, they're looking for anything to reduce your claim. I handle all insurance communication so you don't accidentally hurt your case.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              How Your Rear-End Accident Case Works
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Clear steps, no surprises. You'll always know what's happening with your case.
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {processSteps.map((step) => (
              <div key={step.step} className="relative">
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-100 h-full">
                  <div className="bg-primary text-white w-8 h-8 rounded-full flex items-center justify-center font-bold mb-4">
                    {step.step}
                  </div>
                  <step.icon className="w-8 h-8 text-primary mb-3" />
                  <h3 className="font-heading font-semibold text-secondary mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-sm">{step.description}</p>
                </div>
                {step.step < 5 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 text-slate-300">
                    <ArrowRight className="w-6 h-6" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl font-bold text-secondary mb-6">
                Why Hire Me for Your Rear-End Accident Case
              </h2>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="bg-primary/10 p-3 rounded-full h-fit">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-secondary mb-1">I Know Their Playbook</h3>
                    <p className="text-slate-600">I used to defend insurance companies. I know exactly how they try to minimize rear-end collision claims—and I know how to beat them.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="bg-primary/10 p-3 rounded-full h-fit">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-secondary mb-1">You Work Directly With Me</h3>
                    <p className="text-slate-600">No hand-offs to paralegals or assistants. When you call, you talk to your attorney. I personally handle your case from start to finish.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="bg-primary/10 p-3 rounded-full h-fit">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-secondary mb-1">Constant Communication</h3>
                    <p className="text-slate-600">I return calls and texts quickly. You'll never be left wondering what's happening with your case.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="bg-primary/10 p-3 rounded-full h-fit">
                    <Scale className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-secondary mb-1">No Fee Unless You Win</h3>
                    <p className="text-slate-600">You're already dealing with medical bills and lost wages. My fee comes from your settlement—you pay nothing out of pocket.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="font-heading text-2xl font-bold text-secondary mb-6">
                Free Case Review
              </h3>
              <p className="text-slate-600 mb-6">
                Not sure if you have a case? Wondering if your injuries are "serious enough"? Let's talk. I'll give you honest advice—even if that means you don't need a lawyer.
              </p>
              <div className="space-y-4">
                <a 
                  href="tel:425-675-1224"
                  className="flex items-center justify-center gap-2 bg-primary text-white px-6 py-4 rounded-full font-semibold hover:bg-primary/90 transition-all w-full"
                >
                  <Phone className="w-5 h-5" />
                  Call (425) 675-1224
                </a>
                <a 
                  href="sms:425-675-1224"
                  className="flex items-center justify-center gap-2 border-2 border-secondary text-secondary px-6 py-4 rounded-full font-semibold hover:bg-secondary hover:text-white transition-all w-full"
                >
                  <MessageCircle className="w-5 h-5" />
                  Text Me Now
                </a>
              </div>
              <p className="text-sm text-slate-500 mt-4 text-center">
                I respond personally within 1 business day
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              Common Questions About Rear-End Accident Claims
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-slate-50 p-6 rounded-xl">
                <h3 className="font-heading font-semibold text-secondary mb-3">{faq.question}</h3>
                <p className="text-slate-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Concepts Section - Entity SEO */}
      <section className="py-12 bg-slate-50 border-t border-slate-100">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <h2 className="font-heading text-xl font-semibold text-secondary mb-6 text-center">
            Understanding Rear-End Collision Law
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <a href="https://en.wikipedia.org/wiki/Rear-end_collision" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Rear-End Collision <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Whiplash_(medicine)" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Whiplash Injury <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Soft_tissue_injury" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Soft Tissue Injury <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Personal_injury" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Personal Injury <ExternalLink className="w-3 h-3" />
              </span>
            </a>
          </div>
          <p className="text-xs text-slate-400 text-center mt-4">
            Learn more about these legal and medical concepts on Wikipedia
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-6">
            Don't Settle for Less Than You Deserve
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Insurance companies count on you not knowing your rights. Let me fight for the compensation you're owed.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-primary/90 transition-all"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              Text Me Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RearEndAccidentPage;
